import {
  type TaskBaseFragment,
  TaskBoardColumnTasksDocument,
  type TaskBoardColumnTasksQuery,
  type TaskBoardColumnTasksQueryVariables,
} from "@src/__generated__/graphql";
import {
  BOARD_COLUMN_PAGE_SIZE,
  BOARD_COMPLETE_COLUMN_KEY,
} from "@src/components/modules/resource-planning/tasks/kanban/constants";
import type { TasksKanbanStore } from "@src/components/modules/resource-planning/tasks/kanban/store";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { action, computed, makeObservable, observable } from "mobx";

export type TaskBoardColumnSource = {
  key: string;
  label: string;
  total: number;
  /** Sum of tracked time over every task in the column, in seconds. */
  spentTime: number;
  /** Sum of planned time over every task in the column, in seconds. */
  plannedTime: number;
  backgroundColor?: string;
  foregroundColor?: string;
  /** Status ids the column queries. Only the `Complete` column holds more than one. */
  statusIds?: string[];
};

/** Optimistic drag updates snapshot this so an API error can roll back. */
export type TaskBoardColumnSnapshot = {
  tasks: TaskBaseFragment[];
  total: number;
  spentTime: number;
  plannedTime: number;
};

export class TaskBoardColumnStore {
  readonly key: string;

  @observable.ref label: string;
  @observable total: number;
  @observable spentTime: number;
  @observable plannedTime: number;
  @observable.ref tasks: TaskBaseFragment[] = [];
  @observable.ref backgroundColor?: string;
  @observable.ref foregroundColor?: string;
  @observable.ref statusIds?: string[];
  /** Tasks load lazily — only once the column scrolls into the viewport. */
  @observable hasLoadedOnce = false;
  @observable private page = 1;
  @observable private hasMorePages = true;
  /** True while page 1 is replacing existing cards — keep the old list visible. */
  @observable private isReplacing = false;

  /**
   * Every column queries with the same operation name, so each fetch must
   * carry its own signal — the app-wide aborter link cancels the previous
   * request of any query that shares an operation name, which would leave
   * only the last column's request alive.
   */
  private abortController?: AbortController;
  private inFlightRequest?: Promise<void>;

  private tasksFetcher = new FetchHelper<
    TaskBoardColumnTasksQuery,
    TaskBoardColumnTasksQueryVariables
  >(TaskBoardColumnTasksDocument);

  constructor(
    private readonly boardStore: TasksKanbanStore,
    src: TaskBoardColumnSource,
  ) {
    makeObservable(this);
    this.key = src.key;
    this.label = src.label;
    this.total = src.total;
    this.spentTime = src.spentTime;
    this.plannedTime = src.plannedTime;
    this.backgroundColor = src.backgroundColor;
    this.foregroundColor = src.foregroundColor;
    this.statusIds = src.statusIds;
  }

  @computed
  get isLoading(): boolean {
    return this.tasksFetcher.isLoading.value;
  }

  @computed
  get isCollapsed(): boolean {
    return this.boardStore.settingsStore.isCollapsed(this.key);
  }

  /**
   * The complete column while done tasks are hidden — it stays on the board,
   * but empty, and loads its cards once done tasks are shown again.
   */
  @computed
  get isDisabled(): boolean {
    return (
      this.key === BOARD_COMPLETE_COLUMN_KEY &&
      this.boardStore.filtersStore.hideDoneTasks
    );
  }

  @computed
  get canLoadMore(): boolean {
    return this.hasMorePages && !this.isReplacing && !this.isDisabled;
  }

  /** Cards on screen are being replaced — or are about to be, by a sort. */
  @computed
  get isRefreshing(): boolean {
    return (
      this.isReplacing ||
      (this.hasLoadedOnce && this.boardStore.settingsStore.isSortPending)
    );
  }

  @computed
  get asSource(): TaskBoardColumnSource {
    return {
      key: this.key,
      label: this.label,
      total: this.total,
      spentTime: this.spentTime,
      plannedTime: this.plannedTime,
      backgroundColor: this.backgroundColor,
      foregroundColor: this.foregroundColor,
      statusIds: this.statusIds,
    };
  }

  /** Every page is loaded — the last loaded card is the column's last. */
  @computed
  get isFullyLoaded(): boolean {
    return !this.hasMorePages;
  }

  /** Last card of the whole column, not just of the loaded pages. */
  isLastTask(taskId: string): boolean {
    return this.isFullyLoaded && this.tasks.at(-1)?.id === taskId;
  }

  @computed
  get isEmpty(): boolean {
    return !this.isDisabled && !this.isLoading && this.tasks.length === 0;
  }

  @action.bound
  update(src: TaskBoardColumnSource): void {
    this.label = src.label;
    this.total = src.total;
    this.spentTime = src.spentTime;
    this.plannedTime = src.plannedTime;
    this.backgroundColor = src.backgroundColor;
    this.foregroundColor = src.foregroundColor;
    this.statusIds = src.statusIds;
  }

  @action.bound
  reset(): void {
    this.abortController?.abort(
      "Drops the stale request so reload never appends outdated filters' rows.",
    );
    this.abortController = undefined;
    this.isReplacing = false;
    this.tasks = [];
    this.page = 1;
    this.hasMorePages = true;
    this.hasLoadedOnce = false;
  }

  /** First fetch, triggered by the column entering the horizontal viewport. */
  @action.bound
  loadInitial(): void {
    if (this.hasLoadedOnce) return;
    if (this.inFlightRequest) return;
    if (this.isCollapsed) return;
    if (this.isDisabled) return;

    this.fetchTasks();
  }

  @action.bound
  async reload(): Promise<void> {
    this.abortController?.abort("Keep existing cards on screen.");
    this.abortController = undefined;
    // Dims at once; the request it replaces settles before the new one starts.
    this.isReplacing = true;

    await this.inFlightRequest;
    this.page = 1;
    this.hasMorePages = true;
    await this.fetchTasks({ replace: true });
  }

  @action.bound
  async fetchTasks(options?: { replace?: boolean }): Promise<void> {
    // Cards loaded before done tasks were hidden must not stay on screen.
    if (this.isDisabled) {
      this.reset();
      return;
    }

    const replace = options?.replace ?? false;
    if (!replace && !this.hasMorePages) return;
    if (this.inFlightRequest) return this.inFlightRequest;

    await this.requestTasks(
      { first: BOARD_COLUMN_PAGE_SIZE, page: this.page, replace },
      (tasks) => {
        // A card moved into the loaded range shifts the later pages, so the
        // next page can repeat a card already on screen.
        const loadedIds = new Set(this.tasks.map((task) => task.id));
        this.tasks = replace
          ? tasks
          : [...this.tasks, ...tasks.filter((task) => !loadedIds.has(task.id))];
        this.page += 1;
      },
    );
  }

  /**
   * Reloads every loaded page in one request, keeping the cards on screen
   * and the paging where it is — a card moved past the loaded range shifts
   * the later pages back, so the next page would skip a card.
   */
  @action.bound
  async resync(): Promise<void> {
    await this.inFlightRequest;
    if (this.isDisabled || !this.hasLoadedOnce) return;

    const loadedCount = (this.page - 1) * BOARD_COLUMN_PAGE_SIZE;
    // Silent — the cards stay as they are, only the paging is put right.
    await this.requestTasks(
      { first: loadedCount, page: 1, replace: false },
      (tasks) => {
        this.tasks = tasks;
      },
    );
  }

  /**
   * `replace` dims the column until this request settles — unless a newer one
   * took its place by then, which keeps the column dimmed for itself.
   */
  private requestTasks(
    { replace, ...paging }: { first: number; page: number; replace: boolean },
    apply: (tasks: TaskBaseFragment[]) => void,
  ): Promise<void> {
    this.abortController = new AbortController();
    this.isReplacing = replace;

    const request = this.tasksFetcher
      .fetch(
        { filters: this.boardStore.columnQueryFilters(this), ...paging },
        this.abortController.signal,
      )
      .then(([data, error]) => {
        if (error || !data.tasks) return;

        apply(data.tasks.data);
        this.total = data.tasks.paginatorInfo.total ?? this.total;
        this.hasMorePages = data.tasks.paginatorInfo.hasMorePages;
        this.hasLoadedOnce = true;
      })
      .finally(() => {
        if (this.inFlightRequest !== request) return;

        this.inFlightRequest = undefined;
        this.isReplacing = false;
      });

    this.inFlightRequest = request;
    return request;
  }

  @action.bound
  removeTask(taskId: string): void {
    const removedTask = this.tasks.find((task) => task.id === taskId);
    if (!removedTask) return;

    this.tasks = this.tasks.filter((task) => task.id !== taskId);
    this.adjustTotals(removedTask, -1);
  }

  /**
   * Drops the card from the loaded pages only — it stays in the column, past
   * them, so the totals do not change.
   */
  @action.bound
  detachTask(taskId: string): void {
    this.tasks = this.tasks.filter((task) => task.id !== taskId);
  }

  @action.bound
  prependTask(task: TaskBaseFragment): void {
    this.insertTaskAt(task, 0);
  }

  @action.bound
  insertTaskAt(task: TaskBaseFragment, index: number): void {
    const tasks = [...this.tasks];
    tasks.splice(Math.max(0, Math.min(index, tasks.length)), 0, task);
    this.tasks = tasks;
    this.adjustTotals(task, 1);
  }

  @action.bound
  moveTaskToIndex(taskId: string, index: number): void {
    const from = this.tasks.findIndex((task) => task.id === taskId);
    if (from === -1 || from === index) return;

    const tasks = [...this.tasks];
    const [moved] = tasks.splice(from, 1);
    tasks.splice(index, 0, moved);
    this.tasks = tasks;
  }

  snapshot(): TaskBoardColumnSnapshot {
    return {
      tasks: [...this.tasks],
      total: this.total,
      spentTime: this.spentTime,
      plannedTime: this.plannedTime,
    };
  }

  @action.bound
  restore(snapshot: TaskBoardColumnSnapshot): void {
    this.tasks = snapshot.tasks;
    this.total = snapshot.total;
    this.spentTime = snapshot.spentTime;
    this.plannedTime = snapshot.plannedTime;
  }

  @action.bound
  adjustTotals(task: TaskBaseFragment, delta: 1 | -1): void {
    this.total = Math.max(0, this.total + delta);
    this.spentTime = Math.max(
      0,
      this.spentTime + delta * (task.stats?.spent_time ?? 0),
    );
    this.plannedTime = Math.max(
      0,
      this.plannedTime + delta * (task.stats?.planned_time ?? 0),
    );
  }

  @action.bound
  updateTask(updatedTask: { id: string } & Partial<TaskBaseFragment>): void {
    const index = this.tasks.findIndex((task) => task.id === updatedTask.id);
    if (index === -1) return;

    this.tasks = this.tasks.map((task, taskIndex) =>
      taskIndex === index ? { ...task, ...updatedTask } : task,
    );
  }
}
